import { Flex, VStack, Box, IconButton, Spacer, Text } from '@cad-arc';
import { ConversationParticipantView } from '@mark43/cad-api';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import {
    LexicalComposer,
    PlainTextPlugin,
    ContentEditable,
    OnChangePlugin,
    LexicalErrorBoundary,
    HistoryPlugin,
    $generateHtmlFromNodes,
    CommandListenersPlugin,
    EditorControl,
    EditorControlPlugin,
    FlagNodes,
    FlagPlugin,
    MaxLengthPlugin,
    MentionsPlugin,
    MentionNodes,
    getSelectedMentionItem,
    ClearEditorPlugin,
    clearWithoutFocusing,
} from '../../../common/components/lexical';
import { htmlToText } from 'html-to-text';
import useResizeObserver from 'use-resize-observer';
import { debounce } from 'lodash';
import { useMessagingStore } from '../../hooks/use-messaging-store';
import { observer } from 'mobx-react';
import { Tooltip } from '../../../common/components/tooltip';
import { useWorkspaceStore } from '../../../panels/use-workspace-store';

interface AddMessageInputProps<TFieldValues extends FieldValues>
    extends React.ComponentProps<'div'> {
    control: Control<TFieldValues, unknown>;
    name: FieldPath<TFieldValues>;
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
    isSubmitting: boolean;
    onSubmit?: () => void;
    sendAsParticipantIdentity?: ConversationParticipantView;
    disabledText?: string;
}

export const AddMessageInput = observer(
    <TFieldValues extends FieldValues>({
        control,
        name,
        editorControlRef,
        isSubmitting,
        onSubmit,
        sendAsParticipantIdentity,
        disabledText,
    }: AddMessageInputProps<TFieldValues>): React.JSX.Element => {
        const inputContainerRef = useRef<HTMLDivElement | null>(null);
        const isDisabled = disabledText !== undefined;
        const contentRef = useRef<string>('');
        const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
        const [isFocused, setIsFocused] = useState<boolean>(false);
        const eventAutocompleteOptionsPortalRef = useRef<HTMLDivElement | null>(null);
        const [height, setHeight] = useState<number>(0);
        const [isSendDisabled, setIsSendDisabled] = useState<boolean>(true);
        const [lexicalKey, setLexicalKey] = useState<number>(0);
        const workspacePanelsStore = useWorkspaceStore();

        const submit = useCallback(() => {
            if (onSubmit && editorControlRef.current?.isEditable() && !isSendDisabled) {
                onSubmit();
            }
        }, [onSubmit, editorControlRef, isSendDisabled]);

        const {
            selectedConversationKey,
            isAddUrgentMessageEnabled,
            setIsAddUrgentMessageEnabled,
            toggleAddUrgentMessage,
        } = useMessagingStore();

        const buttonStyle = isAddUrgentMessageEnabled ? {
            backgroundColor: 'var(--arc-colors-urgent-default)',
            color: 'var(--arc-colors-urgent-content)',
        } : {};

        const buttonHoverStyle = isAddUrgentMessageEnabled ? {
            backgroundColor: 'var(--arc-colors-urgent-hover) !important',
        } : {};

        const participantIdentity =
            sendAsParticipantIdentity || selectedConversationKey?.participantIdentity;
        const sendingAsLabel = participantIdentity?.label
            ? `Sending as ${participantIdentity.label}`
            : '';

        useEffect(() => {
            if (
                selectedConversationKey !== undefined &&
                setIsAddUrgentMessageEnabled !== undefined
            ) {
                editorControlRef.current?.clear();
                setIsAddUrgentMessageEnabled(false);
            }
        }, [editorControlRef, selectedConversationKey, setIsAddUrgentMessageEnabled]);

        const debouncedOnResize = debounce((size) => {
            setHeight(size.height ?? 0);
        }, 100);

        useResizeObserver({
            ref: inputContainerRef,
            onResize: debouncedOnResize,
        });

        useEffect(() => {
            return () => {
                debouncedOnResize.cancel();
            };
        }, [debouncedOnResize]);

        useEffect(() => {
            if (lexicalKey > 0 && workspacePanelsStore.isPanelTypeFocused('MESSAGING')) {
                // Something is causing the lexical input to error and need to be re-initialized, but it's not
                // reproducable. Because of this we need to re-initialize and re-focus the lexical input when
                // this happens. However only do so if the messaging panel was focused.
                editorControlRef.current?.focus();
            }
        }, [editorControlRef, lexicalKey, workspacePanelsStore]);

        const isInNewConversationSubPanel = !!sendAsParticipantIdentity;

        const focusedBorderColor = isAddUrgentMessageEnabled
            ? 'var(--arc-colors-urgent-default)'
            : 'var(--arc-colors-selected-default)';

        return (
            <Flex w="100%" ref={inputContainerRef}>
                <VStack
                    data-testid="add-message"
                    display="inline-flex"
                    alignItems="flex-start"
                    padding="var(--arc-space-2)"
                    borderRadius="var(--arc-radii-md)"
                    backgroundColor="surface.foreground"
                    borderWidth={1}
                    width="100%"
                    transitionDuration="var(--arc-transition-duration-normal)"
                    borderColor={isFocused ? focusedBorderColor : 'control.border.default'}
                    boxShadow={isFocused ? `0 0 0 1px ${focusedBorderColor}` : '0 0 0 0'}
                    zIndex={isInNewConversationSubPanel ? 0 : 2}
                >
                    <Controller
                        control={control}
                        name={name}
                        rules={{ required: true }}
                        render={({ field: { onChange, onBlur } }) => {
                            if (isDisabled) {
                                return (
                                    <Tooltip overlay={disabledText} placement="top">
                                        <Box width="100%" height="var(--arc-space-8)"></Box>
                                    </Tooltip>
                                );
                            }
                            return (
                                <LexicalComposer
                                    key={lexicalKey}
                                    initialConfig={{
                                        onError(error) {
                                            if (error?.message?.includes?.('Lexical error #99')) {
                                                // Occasionally this error occurs causing the lexical input to be unusable. The error indicates
                                                // that a node derived from TextNode was added to the root element, which is invalid. It
                                                // needs to be put within an element such as a paragraph before being added to the root node.

                                                // However, it's not clear from the code why or where this is happening and it isn't reproducable
                                                // locally. So this handler recognizes when it happens and re-mounts the LexicalComposer,
                                                // hopefully resolving this issue. The only thing is that this clears the input, but
                                                // a session recording showed that the input was clear when this happened (the user
                                                // was trying to click on the input and nothing was happening), so this is hopefully ok.
                                                setLexicalKey((prevKey) => prevKey + 1);
                                            }
                                        },
                                        nodes: [...FlagNodes, ...MentionNodes],
                                        namespace: 'messaging',
                                    }}
                                >
                                    <>
                                        <PlainTextPlugin
                                            contentEditable={
                                                <ContentEditable
                                                    data-testid="add-message-input"
                                                    style={{
                                                        width: '100%',
                                                        outline: 'none',
                                                        borderStyle: '0',
                                                        maxHeight: 'var(--arc-space-24)',
                                                        overflow: 'auto',
                                                        color: isReadOnly
                                                            ? 'text.secondary'
                                                            : 'text.primary',
                                                    }}
                                                />
                                            }
                                            placeholder={null}
                                            ErrorBoundary={LexicalErrorBoundary}
                                        />
                                        <OnChangePlugin
                                            onChange={(editorState, editor) => {
                                                // This hooks up the Lexical editor to the React Hook Form. When there's a change
                                                // to the Lexical editor, this callback is fired, the Lexical (node-based) data is
                                                // converted to plain text and sent to React Hook Form via its onChange callback.
                                                editorState.read(() => {
                                                    const html = $generateHtmlFromNodes(
                                                        editor,
                                                        null
                                                    );
                                                    const text = htmlToText(html, {
                                                        preserveNewlines: true,
                                                        whitespaceCharacters: '',
                                                    });
                                                    contentRef.current = text;
                                                    setIsSendDisabled(text.trim() === '');
                                                    onChange(text);
                                                });
                                            }}
                                            ignoreSelectionChange
                                        />
                                        <CommandListenersPlugin
                                            onBlur={() => {
                                                setIsFocused(false);
                                                onBlur();
                                            }}
                                            onEditable={(isEditable) => {
                                                if (isEditable) {
                                                    setIsReadOnly(false);
                                                } else {
                                                    setIsReadOnly(true);
                                                }
                                            }}
                                            onEnterDown={submit}
                                            onFocus={() => {
                                                setIsFocused(true);
                                            }}
                                            onSpacePress={() => {
                                                getSelectedMentionItem(
                                                    eventAutocompleteOptionsPortalRef
                                                )?.click();
                                            }}
                                            mentionOptionsPortalRef={
                                                eventAutocompleteOptionsPortalRef
                                            }
                                        />
                                        <EditorControlPlugin ref={editorControlRef} />
                                        {/* FlagPlugin renders the caution and plate flags in the editor */}
                                        <FlagPlugin />
                                        <MentionsPlugin
                                            inputContainerRef={inputContainerRef}
                                            mentionOptionsPortalRef={
                                                eventAutocompleteOptionsPortalRef
                                            }
                                            containerBottom={height + 20}
                                            autocompleteOptions={{ includeClearedEvents: false }}
                                        />
                                        <MaxLengthPlugin maxLength={1024 * 64} />
                                        {/* ClearEditorPlugin add support to clear the editor via dispatching CLEAR_EDITOR_COMMAND */}
                                        <ClearEditorPlugin onClear={clearWithoutFocusing} />
                                        {/* HistoryPlugin allows undo/redo keypress in the editor */}
                                        <HistoryPlugin />
                                    </>
                                </LexicalComposer>
                            );
                        }}
                    />
                    <Flex w="100%">
                        <IconButton
                            style={buttonStyle}
                            _hover={buttonHoverStyle}
                            data-testid="toggle-urgent-message"
                            icon="ExclamationPoint"
                            aria-label="Toggle urgent message"
                            onClick={() => {
                                if (editorControlRef.current?.isEditable()) {
                                    toggleAddUrgentMessage();
                                    editorControlRef.current?.focus();
                                }
                            }}
                            isDisabled={isDisabled || isSubmitting}
                        />
                        <Spacer />
                        {!isDisabled && (
                            <Text
                                data-testid="sending-as-text"
                                fontSize="sm"
                                color="tertiary"
                                style={{marginLeft: 'var(--arc-space-4)'}}
                            >
                                {sendingAsLabel}
                            </Text>
                        )}

                        <Box
                            borderLeft="sm"
                            height="75%"
                            w={0}
                            mx="3"
                            color="border.default"
                        />
                        <IconButton
                            variant="solid"
                            data-testid="send-message-button"
                            icon="Send"
                            aria-label="Send message"
                            onClick={submit}
                            isLoading={isSubmitting}
                            isDisabled={isSendDisabled}
                            style={buttonStyle}
                            _hover={!isSendDisabled ? buttonHoverStyle : undefined}
                        />
                    </Flex>
                </VStack>
            </Flex>
        );
    }
);
